import {
  ChevronDown,
  CircleDollarSign,
  Eye,
  EyeOff,
  File,
  Landmark,
  Upload,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../../context/AuthContext";
import { useSidebar } from "../../../../context/SidebarContext";
import {
  bankingPartnerAPI,
  loanTypeAPI,
} from "../../../../services/AdminPortal/api";
import TopNavbar from "../../TopNavbar";

// Add this mapping for insurance types and categories
const insuranceTypeCategoryMap = {
  "Life Insurance": [
    "Term Life Insurance",
    "Whole Life Insurance",
    "Endowment Policy",
    "Unit-linked Insurance Plans (ULIPs)",
    "Group Life Insurance",
  ],
  "Health Insurance": [
    "Individual Health Insurance",
    "Family Floater Health Insurance",
    "Critical Illness Insurance",
    "Maternity & Newborn Coverage",
    "Senior Citizen Health Insurance",
    "Group Health Insurance",
  ],
  "Motor Insurance": [
    "Third-Party Liability Insurance",
    "Comprehensive Insurance",
    "Own Damage Insurance",
    "Two-Wheeler Insurance",
  ],
  "Home/Property Insurance": [
    "Home Building Insurance",
    "Home Contents Insurance",
    "Fire & Natural Disaster Insurance",
  ],
  "Travel Insurance": [
    "Single Trip Insurance",
    "Multi-trip Insurance",
    "Student Travel Insurance",
    "International Travel Insurance",
    "Honeymoon Travel Insurance",
  ],
  "Commercial Insurance": [
    "Fire & Allied Perils Insurance",
    "Marine Insurance",
    "Liability Insurance",
    "Business Interruption Insurance",
    "Group Employee Insurance",
    "Cyber Insurance",
  ],
  "Women’s Insurance Plans": [
    "Critical Illness Plans for Women",
    "Maternity Insurance",
    "Personal Accident Insurance",
    "Life Insurance Plans for Homemakers",
  ],
  "Child Insurance Plans": [
    "Child Education Plans",
    "Health Insurance for Children",
    "Child Term Plans",
  ],
  "Newly Married Couple Insurance": [
    "Joint Life Insurance Policies",
    "Family Floater Health Insurance",
    "Maternity & Fertility Treatment Coverage",
    "Home Content & Marriage Jewelry Insurance",
    "Travel Insurance",
  ],
};

export default function BankingPartnerForm() {
  const navigate = useNavigate();
  const { sidebarExpanded, isMobile } = useSidebar();
  const { user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  // Form state
  const initialFormData = {
    // Bank Details
    bankName: "",
    bankCategory: "",
    branchName: "",
    branchIFSCCode: "",
    bankAddress: "",
    email: "",
    password: "",
    role: "banking-partner",

    // Representative Details
    representativeName: "",
    representativeDesignation: "",
    representativePhone: "",
    representativeEmail: "",

    // Loan Management
    loanDetails: [],
    // Insurance Management
    insuranceDetails: [],

    // Compliance & Verification
    kycVerification: false,
    mouAgreement: false,
  };
  const initialCurrentLoan = {
    loanType: "",
    eligibilityCriteria: "",
    interestRate: "",
    loanInsurance: "",
    minLoanAmount: "",
    maxLoanAmount: "",
  };
  const initialCurrentInsurance = {
    insuranceType: "",
    insuranceEligibility: "",
    insuranceAmount: "",
    insuranceCategory: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [currentLoan, setCurrentLoan] = useState(initialCurrentLoan);
  const [currentInsurance, setCurrentInsurance] = useState(
    initialCurrentInsurance
  );

  // File state
  const [files, setFiles] = useState({
    kycDocument: null,
    mouDocument: null,
  });

  // UI state
  const [kycFileName, setKycFileName] = useState("");
  const [mouFileName, setMouFileName] = useState("");
  const [loanTypeOptions, setLoanTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  // Add state for selected insurance name and category
  const [selectedInsuranceName, setSelectedInsuranceName] = useState("");
  const [selectedInsuranceCategory, setSelectedInsuranceCategory] =
    useState("");

  // Add state for Cloudinary URLs
  const [kycCloudUrl, setKycCloudUrl] = useState("");
  const [mouCloudUrl, setMouCloudUrl] = useState("");

  useEffect(() => {
    fetchLoanTypes();
  }, []);
  const generatePassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({ ...formData, password });
  };

  const fetchLoanTypes = async () => {
    try {
      const response = await loanTypeAPI.getAll();
      if (response.success) {
        setLoanTypeOptions(response.data);
      } else {
        toast.error("Failed to fetch loan types");
      }
    } catch (error) {
      toast.error("Error fetching loan types");
      console.error("Error fetching loan types:", error);
    }
  };

  const handleCurrentLoanChange = (e) => {
    setCurrentLoan({ ...currentLoan, [e.target.name]: e.target.value });
  };

  const handleCurrentInsuranceChange = (e) => {
    setCurrentInsurance({
      ...currentInsurance,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddLoanCategory = () => {
    // Validate all required fields for the loan
    const errors = [];
    if (!currentLoan.loanType) errors.push("Loan Category");
    if (!currentLoan.eligibilityCriteria) errors.push("Eligibility Criteria");
    if (!currentLoan.minLoanAmount) errors.push("Minimum Amount");
    if (!currentLoan.maxLoanAmount) errors.push("Maximum Amount");
    const min = parseFloat(currentLoan.minLoanAmount);
    const max = parseFloat(currentLoan.maxLoanAmount);
    if (isNaN(min) || isNaN(max) || min >= max)
      errors.push("Valid Min/Max Amount");
    if (errors.length > 0) {
      toast.error(`Please fill in: ${errors.join(", ")}`);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      loanDetails: [
        ...prev.loanDetails,
        {
          ...currentLoan,
          interestRate: currentLoan.interestRate
            ? parseFloat(currentLoan.interestRate)
            : null,
          minLoanAmount: parseFloat(currentLoan.minLoanAmount),
          maxLoanAmount: parseFloat(currentLoan.maxLoanAmount),
        },
      ],
    }));
    setCurrentLoan({
      loanType: "",
      eligibilityCriteria: "",
      interestRate: "",
      loanInsurance: "",
      minLoanAmount: "",
      maxLoanAmount: "",
    });
    toast.success("Loan category added.");
  };

  const handleAddInsuranceType = () => {
    if (!currentInsurance.insuranceType || !currentInsurance.insuranceAmount) {
      toast.error("Please fill in Insurance Type and Amount.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      insuranceDetails: [
        ...prev.insuranceDetails,
        {
          ...currentInsurance,
          insuranceAmount: parseFloat(currentInsurance.insuranceAmount),
        },
      ],
    }));
    setCurrentInsurance({
      insuranceType: "",
      insuranceEligibility: "",
      insuranceAmount: "",
      insuranceCategory: "",
    });
    toast.success("Insurance type added.");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleLoanTypeChange = (e) => {
    const value = e.target.value;
    setCurrentLoan({
      ...currentLoan,
      loanType: value,
    });

    if (errors.loanDetails) {
      setErrors({
        ...errors,
        loanDetails: "",
      });
    }
  };

  const handleFileClick = (inputId) => {
    const fileInput = document.getElementById(inputId);
    if (fileInput) {
      fileInput.click();
    } else {
      console.error(`File input with id ${inputId} not found`);
    }
  };

  const compressImage = (file, maxSizeKB = 500) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const ratio = Math.min(800 / img.width, 800 / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.7);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 1 * 1024 * 1024; // Reduced to 1MB limit
      
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const allowedTypes = ["pdf", "jpg", "jpeg", "png"];
      if (!allowedTypes.includes(fileExtension)) {
        toast.error("Invalid file type. Only PDF, JPG, and PNG are allowed");
        return;
      }

      // Show notification if file is larger than 1MB
      if (file.size > maxSize) {
        toast.warning(`File size is ${(file.size / (1024 * 1024)).toFixed(2)}MB, which exceeds the 1MB limit`);
      }

      let processedFile = file;
      
      // Compress images if they're too large
      if (["jpg", "jpeg", "png"].includes(fileExtension) && file.size > maxSize) {
        try {
          processedFile = await compressImage(file);
          toast.info("Image compressed to reduce file size");
        } catch (error) {
          toast.error("Failed to compress image");
          return;
        }
      }
      
      if (processedFile.size > maxSize) {
        toast.error("File too large. Maximum size is 1MB");
        e.target.value = ''; // Clear the input
        return;
      }

      if (type === "kyc") {
        setKycFileName(file.name);
        setFiles({
          ...files,
          kycDocument: file,
        });
        setFormData((prev) => ({
          ...prev,
          kycVerification: true,
        }));
        if (errors.kycDocument) {
          setErrors({
            ...errors,
            kycDocument: "",
          });
        }
      } else if (type === "mou") {
        setMouFileName(file.name);
        setFiles({
          ...files,
          mouDocument: file,
        });
        setFormData((prev) => ({
          ...prev,
          mouAgreement: true,
        }));
      }

      toast.success("File Added Successfully!");
    }
  };

  const validateIFSC = (ifsc) => {
    // Standard IFSC: 4 letters, 0, 6 alphanumeric
    return /^[A-Z]{4}0[A-Z0-9]{6}$/i.test(ifsc.trim());
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bankName.trim()) newErrors.bankName = "Bank name is required";
    if (!formData.bankCategory)
      newErrors.bankCategory = "Bank category is required";
    if (!formData.branchName.trim())
      newErrors.branchName = "Branch name is required";
    if (!formData.branchIFSCCode.trim()) {
      newErrors.branchIFSCCode = "IFSC code is required";
    }
    if (!formData.bankAddress.trim())
      newErrors.bankAddress = "Bank address is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (!formData.representativeName.trim())
      newErrors.representativeName = "Representative name is required";
    if (!formData.representativeDesignation)
      newErrors.representativeDesignation = "Designation is required";
    if (!formData.representativePhone.trim()) {
      newErrors.representativePhone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.representativePhone.trim())) {
      newErrors.representativePhone =
        "Please enter a valid 10-digit phone number";
    }
    if (!formData.representativeEmail.trim()) {
      newErrors.representativeEmail = "Email is required";
    } else if (
      !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(
        formData.representativeEmail
      )
    ) {
      newErrors.representativeEmail = "Please enter a valid email address";
    }

    if (formData.loanDetails.length === 0)
      newErrors.loanDetails = "At least one loan category is required";

    // Validate min/max per loan
    formData.loanDetails.forEach((loan, index) => {
      if (!loan.minLoanAmount)
        newErrors[`loanDetails.${index}.minLoanAmount`] =
          "Minimum Amount is required";
      if (!loan.maxLoanAmount)
        newErrors[`loanDetails.${index}.maxLoanAmount`] =
          "Maximum Amount is required";
      const min = parseFloat(loan.minLoanAmount);
      const max = parseFloat(loan.maxLoanAmount);
      if (isNaN(min) || isNaN(max) || min >= max) {
        newErrors[`loanDetails.${index}.maxLoanAmount`] =
          "Maximum amount must be greater than minimum amount";
      }
    });

    if (!files.kycDocument) newErrors.kycDocument = "KYC document is required";
    if (!files.mouDocument) newErrors.mouDocument = "MOU document is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "YOUR_UPLOAD_PRESET"); // Replace with your Cloudinary preset
    const res = await fetch(
      "https://api.cloudinary.com/v1_1/CLOUDINARY_CLOUD_NAME/auto/upload",
      {
        method: "POST",
        body: formData,
      }
    );
    const data = await res.json();
    return data.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setIsLoading(true);

    try {
      // Check if required documents are present
      const requiredFiles = ['kycDocument', 'mouDocument'];
      const missingFiles = requiredFiles.filter(fileType => !files[fileType]);
      
      if (missingFiles.length > 0) {
        toast.error(`Missing required documents: ${missingFiles.join(', ')}`);
        setIsLoading(false);
        return;
      }

      const bankingPartnerData = {
        ...formData,
        compliance: {
          ...formData.compliance,
        },
      };

      console.log("Banking Partner Data being sent:", bankingPartnerData);

      const response = await bankingPartnerAPI.create(bankingPartnerData);

      if (response.success && response.data && response.data._id) {
        const bankingPartnerId = String(response.data._id);

        if (
          bankingPartnerId === "undefined" ||
          bankingPartnerId === "null" ||
          !bankingPartnerId.trim()
        ) {
          toast.error(
            "Failed to retrieve a valid ID for the banking partner after creation."
          );
          setIsLoading(false);
          return;
        }

        // Upload documents sequentially
        try {
          console.log("Starting document uploads...");
          
          const uploadFile = async (file, type) => {
            // Add retry logic for uploads
            let retries = 3;
            while (retries > 0) {
              try {
                const uploadResponse = await bankingPartnerAPI.uploadDocument(file, type, bankingPartnerId);
                if (!uploadResponse.success) {
                  throw new Error(`Failed to upload ${type}`);
                }
                return uploadResponse;
              } catch (error) {
                retries--;
                if (retries === 0) throw error;
                console.log(`Retrying upload for ${type}, attempts left: ${retries}`);
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
              }
            }
          };

          // Upload all files sequentially with retry logic
          await uploadFile(files.kycDocument, "kyc");
          await uploadFile(files.mouDocument, "mou");
          
          console.log("All documents uploaded successfully");
          toast.success("Banking partner created successfully!");
          
          // Reset all form state after success
          setFormData(initialFormData);
          setCurrentLoan(initialCurrentLoan);
          setCurrentInsurance(initialCurrentInsurance);
          setSelectedInsuranceName("");
          setSelectedInsuranceCategory("");
          setKycFileName("");
          setMouFileName("");
          setFiles({ kycDocument: null, mouDocument: null });
          setErrors({});
          setSubmitError("");
          
          // Add delay before navigation to ensure toast is visible
          setTimeout(() => {
            const role = user?.role?.toLowerCase();
            if (["admin", "subadmin"].includes(role)) {
              navigate(`/${role}/banking-partner`);
            } else {
              console.warn("Unknown role:", role);
            }
          }, 1500);
        } catch (uploadError) {
          console.error("Error uploading documents:", uploadError);
          toast.error("Document upload failed. Partner creation cancelled.");
          // Delete the created partner since document upload failed
          await bankingPartnerAPI.delete(bankingPartnerId);
          setIsLoading(false);
          return;
        }
      } else {
        const errorMessage =
          response.message ||
          "Failed to create banking partner or vital data missing in response.";
        toast.error(errorMessage);
        console.error(
          "Error creating banking partner or missing _id:",
          response
        );
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      
      if (errorMessage && errorMessage.includes('phoneNumber_1 dup key')) {
        setSubmitError("Phone number already exists. Please use a different phone number.");
      } else {
        setSubmitError(errorMessage || "An unexpected error occurred");
      }
      console.error("Error creating banking partner:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update handleCurrentInsuranceChange to handle both dropdowns
  const handleInsuranceNameChange = (e) => {
    setSelectedInsuranceName(e.target.value);
    setSelectedInsuranceCategory("");
    setCurrentInsurance({
      ...currentInsurance,
      insuranceType: e.target.value,
      insuranceCategory: "",
    });
  };
  const handleInsuranceCategoryChange = (e) => {
    setSelectedInsuranceCategory(e.target.value);
    setCurrentInsurance({
      ...currentInsurance,
      insuranceCategory: e.target.value,
    });
  };

  return (
    <div
      className="min-h-screen bg-black text-white mt-12"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "220px" : "80px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "220px" : "80px"})`,
      }}
    >
      <TopNavbar />

      <div className="p-4 md:p-6">
        <div className="mb-6">
          <span
            className="text-[#FFCA20] cursor-pointer"
            onClick={() => {
              const role = user?.role?.toLowerCase();
              if (["admin", "subadmin"].includes(role)) {
                navigate(`/${role}/banking-partner/`);
              } else {
                console.warn("Unknown role:", role);
              }
            }}
          >
            Banking Partner
          </span>{" "}
          &gt; Create New Banking Partner
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-8">
            <h2 className="flex items-center text-lg mb-4">
              <Landmark size={20} className="text-[#FFCA20] mr-2" /> Bank
              Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm">
                  Bank Name<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="text"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Name"
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.bankName ? "border-red-500" : "border-[#333333]"
                  } rounded`}
                />
                {errors.bankName && (
                  <p className="text-red-500 text-xs mt-1">{errors.bankName}</p>
                )}
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Bank Category<span className="text-[#FFCA20]">*</span>
                </label>
                <div className="relative">
                  <select
                    name="bankCategory"
                    value={formData.bankCategory}
                    onChange={handleInputChange}
                    className={`w-full p-2 bg-[#131313] border ${
                      errors.bankCategory
                        ? "border-red-500"
                        : "border-[#333333]"
                    } rounded appearance-none`}
                  >
                    <option value="">Select Bank Category</option>
                    <option value="Private Bank">Private Bank</option>
                    <option value="Government Bank">Government Bank</option>
                    <option value="Regional Rural Banks">
                      Regional Rural Banks
                    </option>
                    <option value="Foreign Banks">Foreign Banks</option>
                    <option value="Cooperative Banks">Cooperative Banks</option>
                    <option value="NBFC">NBFC</option>
                    <option value="NBFC-MFI">NBFC-MFI</option>
                    <option value="NBFC-HFC">NBFC-HFC</option>
                    <option value="Small Finance Banks">
                      Small Finance Banks
                    </option>
                    <option value="Development Financial Institutions">
                      Development Financial Institutions
                    </option>
                    <option value="Fintech Lenders">Fintech Lenders</option>
                    <option value="Payments Banks">Payments Banks</option>
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-3" />
                  {errors.bankCategory && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bankCategory}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Branch Name<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="text"
                  name="branchName"
                  value={formData.branchName}
                  onChange={handleInputChange}
                  placeholder="Enter Branch name"
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.branchName ? "border-red-500" : "border-[#333333]"
                  } rounded`}
                />
                {errors.branchName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.branchName}
                  </p>
                )}
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Branch IFSC Code<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="text"
                  name="branchIFSCCode"
                  value={formData.branchIFSCCode}
                  onChange={handleInputChange}
                  placeholder="Enter IFSC Code"
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.branchIFSCCode
                      ? "border-red-500"
                      : "border-[#333333]"
                  } rounded`}
                />
                {errors.branchIFSCCode && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.branchIFSCCode}
                  </p>
                )}
              </div>
              <div className="col-span-1 md:col-span-2">
                <label className="block mb-2 text-sm">
                  Bank Address<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="text"
                  name="bankAddress"
                  value={formData.bankAddress}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Address"
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.bankAddress ? "border-red-500" : "border-[#333333]"
                  } rounded`}
                />
                {errors.bankAddress && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.bankAddress}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="mb-8">
            <h2 className="flex items-center text-lg mb-4">
              <User size={20} className="text-[#FFCA20] mr-2" /> Login
              Credentials
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm">
                  Email<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter Email"
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.email ? "border-red-500" : "border-[#333333]"
                  } rounded`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Password<span className="text-[#FFCA20]">*</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter Password"
                    className={`w-full p-2 pr-24 bg-[#131313] border ${
                      errors.password ? "border-red-500" : "border-[#333333]"
                    } rounded`}
                  />
                  <div className="absolute right-2 flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-200"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button
                      type="button"
                      onClick={generatePassword}
                      className="text-[#FFCA20] text-xs hover:text-[#FFD700]"
                    >
                      Generate
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="flex items-center text-lg mb-4">
              <User size={20} className="text-[#FFCA20] mr-2" /> Representative
              Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm">
                  Name<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="text"
                  name="representativeName"
                  value={formData.representativeName}
                  onChange={handleInputChange}
                  placeholder="Enter Representative Name"
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.representativeName
                      ? "border-red-500"
                      : "border-[#333333]"
                  } rounded`}
                />
                {errors.representativeName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.representativeName}
                  </p>
                )}
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Designation<span className="text-[#FFCA20]">*</span>
                </label>
                <div className="relative">
                  <select
                    name="representativeDesignation"
                    value={formData.representativeDesignation}
                    onChange={handleInputChange}
                    className={`w-full p-2 bg-[#131313] border ${
                      errors.representativeDesignation
                        ? "border-red-500"
                        : "border-[#333333]"
                    } rounded appearance-none`}
                  >
                    <option value="">Select Designation</option>
                    <option value="Probationary Officer">
                      Probationary Officer
                    </option>
                    <option value="Loan Officer">Loan Officer</option>
                    <option value="Credit Officer">Credit Officer</option>
                    <option value="Relationship Manage">
                      Relationship Manage
                    </option>
                    <option value="Branch Manager">Branch Manager</option>
                    <option value="Assistant Manager">Assistant Manager</option>
                    <option value="Clerk">Clerk</option>
                    <option value="Specialist Officers">
                      Specialist Officers
                    </option>
                    <option value="Chief/Executive Level Officer">
                      Chief/Executive Level Officer
                    </option>
                    <option value="DSA officer">DSA officer</option>
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-3" />
                  {errors.representativeDesignation && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.representativeDesignation}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Phone Number<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="text"
                  name="representativePhone"
                  value={formData.representativePhone}
                  onChange={handleInputChange}
                  placeholder="Enter Phone Number"
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.representativePhone
                      ? "border-red-500"
                      : "border-[#333333]"
                  } rounded`}
                />
                {errors.representativePhone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.representativePhone}
                  </p>
                )}
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Email ID<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="email"
                  name="representativeEmail"
                  value={formData.representativeEmail}
                  onChange={handleInputChange}
                  placeholder="Enter Email ID"
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.representativeEmail
                      ? "border-red-500"
                      : "border-[#333333]"
                  } rounded`}
                />
                {errors.representativeEmail && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.representativeEmail}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="mb-10">
            <h2 className="flex items-center text-lg mb-4">
              <CircleDollarSign size={20} className="text-[#FFCA20] mr-2" />
              Insurance Management
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-2">
              <div>
                <label className="block mb-2 text-sm">
                  Insurance Name<span className="text-[#FFCA20]">*</span>
                </label>
                <select
                  name="insuranceType"
                  value={selectedInsuranceName}
                  onChange={handleInsuranceNameChange}
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded appearance-none"
                >
                  <option value="">Select Insurance Name</option>
                  {Object.keys(insuranceTypeCategoryMap).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Category<span className="text-[#FFCA20]">*</span>
                </label>
                <select
                  name="insuranceCategory"
                  value={selectedInsuranceCategory}
                  onChange={handleInsuranceCategoryChange}
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded appearance-none"
                  disabled={!selectedInsuranceName}
                >
                  <option value="">Select Category</option>
                  {selectedInsuranceName &&
                    insuranceTypeCategoryMap[selectedInsuranceName].map(
                      (cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      )
                    )}
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Eligibility Criteria<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="text"
                  name="insuranceEligibility"
                  value={currentInsurance.insuranceEligibility}
                  onChange={handleCurrentInsuranceChange}
                  placeholder="Enter eligibility criteria"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Amount<span className="text-[#FFCA20]">*</span>
                </label>
                <div className="relative">
                  <span className="absolute top-2 left-2">₹</span>
                  <input
                    type="number"
                    name="insuranceAmount"
                    value={currentInsurance.insuranceAmount}
                    onChange={handleCurrentInsuranceChange}
                    placeholder="Enter Amount"
                    className="w-full p-2 pl-6 bg-[#131313] border border-[#333333] rounded"
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleAddInsuranceType}
              className="border border-[#FFCA20]  px-4 py-2 rounded bg-[#FFCA20] text-black transition font-medium mt-2"
            >
              Add Insurance Type
            </button>
            {formData.insuranceDetails.length > 0 && (
              <div className="mt-4 p-4 border border-dashed border-[#333333] rounded">
                <h3 className="font-semibold mb-2">Added Insurance Types:</h3>
                <ul>
                  {formData.insuranceDetails.map((insurance, index) => (
                    <li key={index} className="text-sm text-gray-300 mb-1">
                      - {insurance.insuranceType} /{" "}
                      {insurance.insuranceCategory}: Amount: ₹
                      {insurance.insuranceAmount}, Eligibility:{" "}
                      {insurance.insuranceEligibility || "N/A"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="mb-10">
            <h2 className="flex items-center text-lg mb-4">
              <CircleDollarSign size={20} className="text-[#FFCA20] mr-2" />
              Loan Management
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-2">
              <div>
                <label className="block mb-2 text-sm">
                  Loan Category<span className="text-[#FFCA20]">*</span>
                </label>
                <select
                  name="loanType"
                  value={currentLoan.loanType}
                  onChange={handleLoanTypeChange}
                  className={`w-full p-2 bg-[#131313] border ${
                    errors.loanDetails ? "border-red-500" : "border-[#333333]"
                  } rounded appearance-none`}
                >
                  <option value="">Select Loan Type</option>
                  {/* Add static options first */}
                  <option value="Home Loan">Home Loan</option>
                  <option value="Personal Loan">Personal Loan</option>
                  <option value="Vehicle Loan">Vehicle Loan</option>
                  <option value="Plot Loan">Plot Loan</option>
                  <option value="Home Loan Construction">
                    Home Loan Construction
                  </option>
                  <option value="Loan Against Property">
                    Loan Against Property
                  </option>
                  <option value="Business Loan">Business Loan</option>

                  {/* Then add dynamic options */}
                  {loanTypeOptions.map((loanType) => (
                    <option key={loanType._id} value={loanType._id}>
                      {loanType.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Eligibility Criteria<span className="text-[#FFCA20]">*</span>
                </label>
                <input
                  type="text"
                  name="eligibilityCriteria"
                  value={currentLoan.eligibilityCriteria}
                  onChange={handleCurrentLoanChange}
                  placeholder="Enter eligibility criteria"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm">Interest Rate</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    name="interestRate"
                    value={currentLoan.interestRate}
                    onChange={handleCurrentLoanChange}
                    placeholder="Enter interest p.a."
                    className="w-full p-2 pr-6 bg-[#131313] border border-[#333333] rounded"
                  />
                  <span className="absolute right-2 top-2 text-sm">%</span>
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Insurance (if applicable)
                </label>
                <select
                  name="loanInsurance"
                  value={currentLoan.loanInsurance}
                  onChange={handleCurrentLoanChange}
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded appearance-none"
                >
                  <option value="">Select Insurance</option>
                  {formData.insuranceDetails.map((ins, idx) => (
                    <option
                      key={idx}
                      value={ins.insuranceType + "|" + ins.insuranceCategory}
                    >
                      {ins.insuranceType} / {ins.insuranceCategory}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-2 text-sm">
                  Minimum Amount<span className="text-[#FFCA20]">*</span>
                </label>
                <div className="relative">
                  <span className="absolute top-2 left-2">₹</span>
                  <input
                    type="number"
                    name="minLoanAmount"
                    value={currentLoan.minLoanAmount}
                    onChange={handleCurrentLoanChange}
                    placeholder="Enter Minimum Amount"
                    className="w-full p-2 pl-6 bg-[#131313] border border-[#333333] rounded"
                  />
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm">
                  Maximum Amount<span className="text-[#FFCA20]">*</span>
                </label>
                <div className="relative">
                  <span className="absolute top-2 left-2">₹</span>
                  <input
                    type="number"
                    name="maxLoanAmount"
                    value={currentLoan.maxLoanAmount}
                    onChange={handleCurrentLoanChange}
                    placeholder="Enter Maximum Amount"
                    className="w-full p-2 pl-6 bg-[#131313] border border-[#333333] rounded"
                  />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleAddLoanCategory}
              className="border border-[#FFCA20]  px-4 py-2 rounded bg-[#FFCA20] text-black transition font-medium mt-2"
            >
              Add Loan Category
            </button>
            {errors.loanDetails && (
              <p className="text-red-500 text-xs mt-1">{errors.loanDetails}</p>
            )}

            {formData.loanDetails.length > 0 && (
              <div className="mt-4 p-4 border border-dashed border-[#333333] rounded">
                <h3 className="font-semibold mb-2">Added Loan Categories:</h3>
                <ul>
                  {formData.loanDetails.map((loan, index) => {
                    let loanTypeLabel = loanTypeOptions.find(
                      (opt) => opt._id === loan.loanType
                    )?.name;
                    // If not found in dynamic options, check static options
                    if (!loanTypeLabel) {
                      const staticOptions = [
                        "Home Loan",
                        "Personal Loan",
                        "Vehicle Loan",
                        "Plot Loan",
                        "Home Loan Construction",
                        "Loan Against Property",
                        "Business Loan",
                      ];

                      if (staticOptions.includes(loan.loanType)) {
                        loanTypeLabel = loan.loanType;
                      } else {
                        loanTypeLabel = loan.loanType || "Unknown Type";
                      }
                    }
                    return (
                      <li key={index} className="text-sm text-gray-300 mb-1">
                        - {loanTypeLabel}: Eligibility:{" "}
                        {loan.eligibilityCriteria}, Rate:{" "}
                        {loan.interestRate || "N/A"}%, Insurance:{" "}
                        {loan.loanInsurance || "N/A"}, Min: ₹
                        {loan.minLoanAmount}, Max: ₹{loan.maxLoanAmount}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <div className="mb-8">
            <h2 className="flex items-center text-lg mb-4">
              <File size={20} className="text-[#FFCA20] mr-2" /> Compliance &
              Verification
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between items-center mb-2 py-2 px-2 border border-customBorder rounded-lg">
                  <label className="text-sm">KYC Verification</label>
                  <input
                    type="checkbox"
                    name="kycVerification"
                    checked={formData.kycVerification}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                </div>
                <div
                  className={`p-4 border ${
                    errors.kycDocument ? "border-red-500" : "border-[#333333]"
                  } rounded mb-2`}
                >
                  <p className="text-sm mb-2">
                    Upload KYC Documents
                    <span className="text-[#FFCA20]">*</span>
                  </p>
                  <div
                    onClick={() => handleFileClick("kycDocument")}
                    className="flex flex-col items-center justify-center p-4 border border-dashed border-[#333333] rounded bg-[#131313] cursor-pointer"
                  >
                    <Upload size={24} className="text-[#FFCA20] mb-2" />
                    <button
                      type="button"
                      onClick={() => handleFileClick("kycDocument")}
                      className="text-sm text-center mb-1"
                    >
                      Click to browse files
                    </button>
                    <p className="text-xs text-gray-400">
                      Supported formats: PDF, JPG, PNG (Max 1 MB)
                    </p>
                    <input
                      type="file"
                      id="kycDocument"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, "kyc")}
                      className="hidden"
                    />
                    {kycFileName && (
                      <p className="text-sm text-white mt-2">
                        File Selected: <strong>{kycFileName}</strong>
                      </p>
                    )}
                    {kycCloudUrl && (
                      <a
                        href={kycCloudUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-green-400 text-xs mt-2"
                      >
                        KYC Document Uploaded: View
                      </a>
                    )}
                  </div>
                  {errors.kycDocument && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.kycDocument}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2 py-2 px-2 border border-customBorder rounded-lg">
                  <label className="text-sm">MoU Agreement</label>
                  <input
                    type="checkbox"
                    name="mouAgreement"
                    checked={formData.mouAgreement}
                    onChange={handleInputChange}
                    className="mr-2 border border-customYellow"
                  />
                </div>
                <div className="p-4 border border-[#333333] rounded mb-2">
                  <p className="text-sm mb-2">Upload Agreement Documents</p>
                  <div
                    onClick={() => handleFileClick("mouDocument")}
                    className="flex flex-col items-center justify-center p-4 border border-dashed border-[#333333] rounded bg-[#131313] cursor-pointer"
                  >
                    <Upload size={24} className="text-[#FFCA20] mb-2" />
                    <button
                      type="button"
                      onClick={() => handleFileClick("mouDocument")}
                      className="text-sm text-center mb-1"
                    >
                      Click to browse files
                    </button>
                    <p className="text-xs text-[#616161]">
                      Supported formats: PDF, JPG, PNG (Max 10 MB)
                    </p>
                    <input
                      type="file"
                      id="mouDocument"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, "mou")}
                      className="hidden"
                    />
                    {mouFileName && (
                      <p className="text-sm text-white mt-2">
                        File Selected: <strong>{mouFileName}</strong>
                      </p>
                    )}
                    {mouCloudUrl && (
                      <a
                        href={mouCloudUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-green-400 text-xs mt-2"
                      >
                        MoU Document Uploaded: View
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {submitError && (
            <div className="mb-4 p-3 bg-red-900/20 border border-red-500 rounded text-red-400 text-sm">
              {submitError}
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
            <button
              type="button"
              className="px-4 py-2 border border-[#333333] rounded order-2 sm:order-1"
              onClick={() => {
                const role = user?.role?.toLowerCase();
                if (["admin", "subadmin"].includes(role)) {
                  navigate(`/${role}/banking-partner/`);
                } else {
                  console.warn("Unknown role:", role);
                }
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#FFCA20] text-black font-medium rounded order-1 sm:order-2 flex items-center justify-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-black"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Create Banking Partner"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
